import React from 'react';
import { MotifInput } from '@ey-xd/motif-react';

const OWNERSHIP_INPUT_REGEX = /^\d*\.?\d*$/;
const MAX_DECIMAL_PLACES = 10;
const MIN_POSITIVE_OWNERSHIP = 1 / Math.pow(10, MAX_DECIMAL_PLACES);
const INVALID_OWNERSHIP_INPUT_ERROR =
  'Enter only numbers or decimals.';
const MAX_DECIMAL_PLACES_ERROR =
  `Maximum ${MAX_DECIMAL_PLACES} decimal places allowed.`;
const MIN_POSITIVE_OWNERSHIP_ERROR =
  `Value must be at least ${MIN_POSITIVE_OWNERSHIP.toFixed(MAX_DECIMAL_PLACES)}.`;

// Helper to format small numbers as decimals
function formatNumber(val) {
  if (typeof val === 'number') {
    if (val !== 0 && Math.abs(val) < 0.0001) {
      return val.toFixed(MAX_DECIMAL_PLACES).replace(/\.?0+$/, '');
    }
    return val.toString();
  }
  return val || '';
}

export default (props) => {
  const initialValue = props.data ? props.data[props.colDef.field] : '';
  const [value, setValue] = React.useState(formatNumber(initialValue));
  const [error, setError] = React.useState('');

  const getDecimalPlaces = (input = '') => {
    if (!input.includes('.')) {
      return 0;
    }

    return input.split('.')[1].length;
  };

  const validateInput = (input) => {
    // Check for empty/null when propertyTrackingID exists
    if (
      (input === '' || input === null || input === undefined) &&
      props.data &&
      props.data.propertyTrackingID
    ) {
      return 'Ownership % is required.';
    }

    if (input === '.' || !OWNERSHIP_INPUT_REGEX.test(input)) {
      return INVALID_OWNERSHIP_INPUT_ERROR;
    }

    if (getDecimalPlaces(input) > MAX_DECIMAL_PLACES) {
      return MAX_DECIMAL_PLACES_ERROR;
    }

    const numberValue = Number(input);

    if (Number.isNaN(numberValue)) {
      return INVALID_OWNERSHIP_INPUT_ERROR;
    }

    if (numberValue <= 0) {
      return 'Value must be greater than 0.';
    }

    if (numberValue < MIN_POSITIVE_OWNERSHIP) {
      return MIN_POSITIVE_OWNERSHIP_ERROR;
    }

    return '';
  };

  const handleChange = (e) => {
    let inputValue = e.target.value;

    if (!OWNERSHIP_INPUT_REGEX.test(inputValue)) {
      const validationError = INVALID_OWNERSHIP_INPUT_ERROR;
      setError(validationError);

      if (props.onValidationError) {
        props.onValidationError(validationError, props.data.uniqueKey);
      }

      return;
    }

    if (inputValue.includes('.')) {
      const [, decimalPart = ''] = inputValue.split('.');

      if (decimalPart.length > MAX_DECIMAL_PLACES) {
        return;
      }
    }

    const parsedValue = Number(inputValue);

    // If value is greater than 100, set it to 100
    if (inputValue !== '' && !Number.isNaN(parsedValue) && parsedValue > 100) {
      inputValue = '100';
    }

    setValue(inputValue);

    const validationError = validateInput(inputValue);
    setError(validationError);

    if (props.onValidationError) {
      props.onValidationError(validationError, props.data.uniqueKey);
    }

    let normalizedValue = inputValue;

    if (inputValue === '.') {
      normalizedValue = '';
    } else if (inputValue.endsWith('.') && inputValue.length > 1) {
      normalizedValue = inputValue.slice(0, -1);
    }

    if (normalizedValue.includes('.')) {
      const [integerPart = '', decimalPart = ''] = normalizedValue.split('.');

      if (decimalPart.length > 4 && /^0+$/.test(decimalPart.slice(4))) {
        normalizedValue = `${integerPart}.${decimalPart.slice(0, 4)}`;
      }
    }

    props.handleOwnershipPercentageChange(
      normalizedValue,
      props.data.uniqueKey,
      props.colDef.field
    );
  };

  const handleFocus = () => {
    const validationError = validateInput(value);
    setError(validationError);

    if (props.onValidationError) {
      props.onValidationError(validationError, props.data.uniqueKey);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        overflow: 'visible',
      }}
    >
      <MotifInput
        type="text"
        name={`ownershipPercentage.${props.data.uniqueKey}`}
        id={`ownershipPercentage.${props.data.uniqueKey}`}
        onChange={handleChange}
        onFocus={handleFocus}
        value={value}
        hideClearButton={true}
        placeholder="100.00"
        data-testid="txtOwnershipPercentage"
        maxLength={13}
        style={{
          width: '100%',
          marginTop: '10px',
          marginLeft: '8px',
          marginRight: '8px',
          height: error ? '70%' : '42px',
          textAlign: 'left',
        }}
      />
      {error && (
        <span
          style={{
            color: 'red',
            fontSize: '12px',
            marginTop: '0px',
            marginLeft: '10px',
            marginBottom: '2px',
            minHeight: '38px', // Ensures space is reserved for the error message
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};
